import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Check, Copy } from 'lucide-react';

export function AuthenticatorSecret({
  secret,
  otpauthUri,
  variant,
}: {
  secret: string;
  otpauthUri: string;
  variant: 'desk' | 'login';
}) {
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const login = variant === 'login';

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(otpauthUri, { margin: 1, width: 196, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setQr(url);
      })
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [otpauthUri]);

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className={login ? 'rounded-2xl border border-slate-700 bg-slate-950 p-3 text-left' : 'rounded-2xl border border-line bg-shell p-3'}>
      <p className={login ? 'text-[11px] font-semibold uppercase tracking-wide text-slate-400' : 'text-[11px] font-semibold uppercase tracking-wide text-muted'}>
        Scan the QR code or enter the secret
      </p>
      {qr && <img src={qr} alt="Authenticator QR code" className="mx-auto mt-3 h-40 w-40 rounded-xl bg-white p-2" />}
      <div className="mt-3 flex items-center gap-2">
        <code className={login
          ? 'min-w-0 flex-1 break-all rounded-xl bg-slate-900 px-2 py-2 font-mono text-[11px] leading-5 text-white'
          : 'min-w-0 flex-1 break-all rounded-xl border border-line bg-card px-2 py-2 font-mono text-[11px] leading-5 text-ink'}>
          {secret}
        </code>
        <button
          type="button"
          onClick={() => void copySecret()}
          title="Copy secret"
          className={login
            ? 'shrink-0 rounded-xl border border-slate-700 p-2 text-slate-300 hover:text-white'
            : 'shrink-0 rounded-xl border border-line p-2 text-muted hover:text-ink'}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>
      <p className={login ? 'mt-2 text-[11px] leading-4 text-slate-500' : 'mt-2 text-[11px] leading-4 text-muted'}>
        Use a 6-digit authenticator app. The secret is shown once. Confirm with a code from the new entry.
      </p>
    </div>
  );
}
